import React from "react";
import { useTranslation } from "react-i18next";
import { SectionHeader } from "../components/UI";
import { motion } from "framer-motion";
import { Target, Compass, History, ScrollText, Shield, Award } from "lucide-react";

export const PageHero = ({ eyebrow, title, subtitle, image }) => (
  <section className="relative pt-40 pb-24 md:pt-52 md:pb-32 overflow-hidden bg-[#0B1120]">
    <div className="absolute inset-0">
      <img src={image} alt="" className="w-full h-full object-cover opacity-40"/>
      <div className="absolute inset-0 hero-gradient"></div>
    </div>
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
      <div className="eyebrow" style={{color:'#00E5FF'}}>{eyebrow}</div>
      <h1 className="mt-6 font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-4xl leading-[1.05]">{title}</h1>
      {subtitle && <p className="mt-8 max-w-3xl text-lg text-white/85 leading-relaxed">{subtitle}</p>}
    </div>
  </section>
);

export default function About() {
  const { t } = useTranslation();
  const values = t("about.values", { returnObjects: true });
  return (
    <div data-testid="page-about">
      <PageHero eyebrow="About EEMA" title={t("about.hero_title")} subtitle={t("about.hero_subtitle")} image="https://images.pexels.com/photos/8463167/pexels-photo-8463167.jpeg"/>
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} className="premium-card p-10">
            <div className="h-12 w-12 rounded-sm bg-[#003399]/5 flex items-center justify-center mb-6"><Target className="text-[#003399]" strokeWidth={1.5}/></div>
            <div className="eyebrow">{t("about.mission_title")}</div>
            <p className="mt-4 text-lg text-[#0B1120] leading-relaxed">{t("about.mission_text")}</p>
          </motion.div>
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} className="premium-card p-10">
            <div className="h-12 w-12 rounded-sm bg-[#003399]/5 flex items-center justify-center mb-6"><Compass className="text-[#003399]" strokeWidth={1.5}/></div>
            <div className="eyebrow">{t("about.vision_title")}</div>
            <p className="mt-4 text-lg text-[#0B1120] leading-relaxed">{t("about.vision_text")}</p>
          </motion.div>
        </div>
      </section>
      <section className="py-24 md:py-32 bg-[#F5F7FB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader eyebrow="Values" title={t("about.values_title")} />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={v.name} className="premium-card p-8" data-testid={`value-${i}`}>
                <div className="font-display text-[#00E5FF] font-bold text-3xl mb-4">0{i+1}</div>
                <h3 className="font-display font-semibold text-lg text-[#0B1120] mb-3">{v.name}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 grid grid-cols-1 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4"><History className="text-[#003399]" strokeWidth={1.5}/><div className="eyebrow">History</div></div>
            <h2 className="font-display font-semibold text-3xl md:text-4xl">{t("about.history_title")}</h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">{t("about.history_text")}</p>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-4"><Shield className="text-[#003399]" strokeWidth={1.5}/><div className="eyebrow">Governance</div></div>
            <h2 className="font-display font-semibold text-3xl md:text-4xl">{t("about.governance_title")}</h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">{t("about.governance_text")}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
